/* Present native cart rewards and verified cart discounts without changing checkout. */
(() => {
  'use strict';
  const rootSelector = '#monster-upsell-cart';
  const giftIds = new Set(['67616258359511', '67616356073687', '67617292484823']);
  let timer;
  let reading = false;
  let lastRead = 0;
  let cachedCart;
  let version = 0;
  let settleTimer;
  let retryTimer;
  const money = (value, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(value / 100);
  const isGift = (item) => giftIds.has(String(item.variant_id || item.id)) || Boolean(item.properties?._monster_free_gift) || String(item.sku || '').startsWith('FREE-EG25-SLOT');
  function promoSavings(cart) {
    const paid = cart.items.filter((item) => !isGift(item));
    const gifts = cart.items.filter(isGift);
    const original = paid.reduce((total, item) => total + (Number.isFinite(item.original_line_price) ? item.original_line_price : 0), 0);
    // Zero-price gift lines contribute no money to total_price, which includes order discounts.
    if (Number.isFinite(cart.total_price) && gifts.every((item) => item.final_line_price === 0)) return Math.max(0, original - cart.total_price);
    return paid.reduce((total, item) => total + (Number.isFinite(item.original_line_price) && Number.isFinite(item.final_line_price) ? Math.max(0, item.original_line_price - item.final_line_price) : 0), 0);
  }

  function text(node, value) {
    if (node.textContent !== value) node.textContent = value;
  }
  function render(cart) {
    const root = document.querySelector(rootSelector);
    const footer = root?.querySelector('#mu-checkout-container');
    if (!footer) return;
    let summary = footer.querySelector('[data-sb-cart-offer]');
    if (!summary) {
      summary = document.createElement('div');
      summary.dataset.sbCartOffer = 'true';
      summary.setAttribute('aria-live', 'polite');
      footer.prepend(summary);
    }
    // Native coupon removal can leave the string '0' as a non-offer sentinel.
    const codes = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).filter((entry) => entry && typeof entry.code === 'string' && entry.code.trim() !== '' && entry.code.trim() !== '0');
    const savings = promoSavings(cart);
    const label = codes.map((entry) => entry.code).join(', ');
    const parts = [];
    if (label) parts.push((codes.some((entry) => entry.applicable === true) ? 'Code applied: ' : 'Code saved: ') + label);
    if (savings > 0) parts.push('Discount savings: ' + money(savings, cart.currency));
    if (codes.length && !codes.some((entry) => entry.applicable === true)) parts.push('Offer saved. Eligibility depends on the items in your cart.');
    text(summary, parts.join(' · '));
    summary.hidden = !parts.length;
    renderTrust(root);
    // Coupon entry stays collapsed behind the native toggle (owner, 2026-10-18); applied codes still show in the summary above.
  }
  function renderTrust(root) {
    const badges = root.querySelector('.mu-badges');
    if (!badges) return;
    let trust = badges.querySelector('[data-sb-cart-trust]');
    if (!trust) {
      trust = document.createElement('div');
      trust.dataset.sbCartTrust = 'true';
      const guarantee = document.createElement('a');
      guarantee.href = '/policies/refund-policy';
      guarantee.textContent = 'Guarantee terms';
      trust.append(document.createTextNode('Faulty product? Product refund or 1.5× store credit. '), guarantee);
      badges.append(trust);
    }
    // Reviews line sits directly under the Checkout button, where purchase doubt peaks.
    let aggregate = root.querySelector('[data-sb-cart-reviews]');
    const checkout = root.querySelector('#mu-checkout-button');
    if (!aggregate && checkout) {
      const slot = document.createElement('div');
      slot.dataset.sbCartTrust = 'reviews';
      aggregate = document.createElement('a');
      aggregate.dataset.sbCartReviews = 'true';
      aggregate.href = '/pages/top-smoke-bomb-reviews';
      aggregate.hidden = true;
      slot.append(aggregate);
      checkout.insertAdjacentElement('afterend', slot);
    }
    if (!aggregate) return;
    const grid = document.querySelector('.jdgm-widget[data-widget="reviews-grid"][data-review-selection="all"][data-products=""]');
    const bootstrap = document.querySelector('[data-sb-review-aggregate]');
    const rating = Number(grid?.getAttribute('data-shop-average-rating') || bootstrap?.dataset.rating);
    const count = Number(grid?.getAttribute('data-shop-review-count') || bootstrap?.dataset.count);
    const valid = rating > 0 && rating <= 5 && Number.isSafeInteger(count) && count > 0;
    aggregate.hidden = !valid;
    if (valid) text(aggregate, rating.toFixed(2) + '/5 from ' + count.toLocaleString() + ' reviews');
  }

  async function refresh() {
    const root = document.querySelector(rootSelector);
    if (!root?.querySelector('#monster-cart-wrapper')) return;

    if (reading || Date.now() - lastRead < 2000) return;
    reading = true;
    const requestedVersion = version;
    lastRead = Date.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (!Array.isArray(cart.items) || typeof cart.item_count !== 'number') return;
      if (requestedVersion !== version) return;
      cachedCart = cart;
      render(cart);
    } catch (_) {
      // Native cart controls remain available if this optional summary cannot load.
    } finally {
      clearTimeout(timeout); reading = false;
      if (requestedVersion !== version) { lastRead = 0; schedule(); }
    }
  }
  const schedule = () => { clearTimeout(timer); timer = setTimeout(refresh, 180); };
  function settledRefresh() {
    version += 1;
    cachedCart = null;
    const oldSummary = document.querySelector(rootSelector + ' [data-sb-cart-offer]');
    if (oldSummary) oldSummary.hidden = true;
    lastRead = 0;
    schedule();
    clearTimeout(settleTimer);
    clearTimeout(retryTimer);
    settleTimer = setTimeout(() => { lastRead = 0; refresh(); }, 700);
    retryTimer = setTimeout(() => { lastRead = 0; refresh(); }, 1800);
  }
  const watched = new WeakSet();
  function watch(root) {
    if (watched.has(root)) return;
    watched.add(root);
    new MutationObserver((records) => {
      if (records.every((record) => {
        if (record.target.closest?.('[data-sb-cart-offer], [data-sb-cart-trust]')) return true;
        const changed = [...record.addedNodes, ...record.removedNodes];
        return record.type === 'childList' && changed.length > 0 && changed.every((node) => node.nodeType === 1 && node.matches('[data-sb-cart-offer], [data-sb-cart-trust]'));
      })) return;
      settledRefresh();
    }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'data-open', 'class'] });
    root.addEventListener('change', settledRefresh);
    root.addEventListener('click', (event) => {
      if (event.target.closest('#mu-items-container, #mu-coupon')) settledRefresh();
    });
    schedule();
  }
  document.querySelectorAll(rootSelector).forEach(watch);
  new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.matches(rootSelector)) watch(node);
      node.querySelectorAll(rootSelector).forEach(watch);
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/* SB cartfix 2026-10-17: (1) Monster bundle pickers follow the selected variant,
   (2) Monster drawer variant change merges into an existing line instead of dropping quantity,
   (3) /cart page quantity changes target the right line by key, then reload so Monster syncs gifts. */
(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const setSelect = (select, value) => {
    if (select.value === value) return;
    Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set.call(select, value);
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // (1) Bundle picker defaults follow the product form's selected variant.
  if (/\/products\//.test(location.pathname)) {
    let product;
    let lastVariant = '';
    const synced = new WeakSet();
    const handle = location.pathname.split('/products/')[1]?.split(/[/?#]/)[0];
    const formVariant = () => {
      const upsell = document.getElementById('mu_product_upsell');
      const form = upsell?.closest('form[action*="/cart/add"]') || upsell?.parentElement?.closest('section')?.querySelector('form[action*="/cart/add"]') || document.querySelector('main form[action*="/cart/add"]');
      const input = form?.querySelector('[name="id"]');
      return input ? String(input.value) : '';
    };
    const sync = (force) => {
      const upsell = document.getElementById('mu_product_upsell');
      if (!upsell || !product) return;
      const id = formVariant();
      const variant = product.variants.find((v) => String(v.id) === id);
      if (!variant) return;
      const changed = id !== lastVariant;
      lastVariant = id;
      upsell.querySelectorAll('select').forEach((select) => {
        if (!force && !changed && synced.has(select)) return;
        synced.add(select);
        const values = [...select.options].map((o) => o.value);
        const match = variant.options.find((o) => values.includes(o)) || (values.includes(String(variant.id)) ? String(variant.id) : null);
        if (match) setSelect(select, match);
      });
    };
    if (handle) {
      fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => { if (p && Array.isArray(p.variants)) { product = p; sync(true); } })
        .catch(() => {});
      let t;
      const later = () => { clearTimeout(t); t = setTimeout(() => sync(false), 60); };
      document.addEventListener('click', (e) => { if (!e.target.closest('#mu_product_upsell')) later(); }, true);
      document.addEventListener('change', (e) => { if (!e.target.closest('#mu_product_upsell')) later(); }, true);
      new MutationObserver(later).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  // (2) Monster drawer: changing a line to a variant already in the cart merges quantities.
  const prev = new WeakMap();
  const remember = (e) => { const s = e.target.closest?.('#monster-upsell-cart .mu-item-variants select'); if (s) prev.set(s, s.value); };
  document.addEventListener('focusin', remember, true);
  document.addEventListener('pointerdown', remember, true);
  document.addEventListener('change', async (e) => {
    const select = e.target.closest?.('#monster-upsell-cart .mu-item-variants select');
    if (!select) return;
    const to = String(select.value);
    const others = [...document.querySelectorAll('#monster-upsell-cart .mu-item-variants select')].filter((s) => s !== select);
    if (!others.some((s) => String(s.value) === to)) return; // new variant: Monster's own swap is safe
    e.stopImmediatePropagation();
    const from = prev.get(select) || [...select.options].find((o) => o.defaultSelected)?.value;
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      const isGift = (i) => Boolean(i.properties?._monster_free_gift) || String(i.sku || '').startsWith('FREE-EG25-SLOT');
      const paid = cart.items.filter((i) => !isGift(i));
      const target = paid.find((i) => String(i.variant_id) === to);
      const optionIds = [...select.options].map((o) => String(o.value));
      const otherIds = others.map((s) => String(s.value));
      const source = paid.find((i) => from && String(i.variant_id) === String(from) && i !== target)
        || paid.find((i) => i !== target && optionIds.includes(String(i.variant_id)) && !otherIds.includes(String(i.variant_id)));
      if (target && source) {
        const updates = {};
        updates[target.key] = target.quantity + source.quantity;
        updates[source.key] = 0;
        await fetch(root + 'cart/update.js', { method: 'POST', headers: json, body: JSON.stringify({ updates }) });
      }
    } catch (_) { /* fall through to reload */ }
    location.reload();
  }, true);

  // (3) /cart page: change the intended line by key, then reload once so Monster re-syncs gift lines.
  if (location.pathname.replace(/\/$/, '').endsWith('/cart')) {
    let busy = false;
    let pending;
    const apply = async (control, quantity) => {
      if (busy) return;
      busy = true;
      const variantId = String(control.dataset.quantityVariantId || control.closest('.cart-item')?.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId || '');
      const rows = [...document.querySelectorAll('cart-items [data-quantity-variant-id]')];
      const nth = rows.filter((r) => String(r.dataset.quantityVariantId) === variantId).indexOf(control.closest('.cart-item')?.querySelector('[data-quantity-variant-id]') || control);
      try {
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        const line = cart.items.filter((i) => String(i.variant_id) === variantId)[Math.max(0, nth)];
        if (line) await fetch(root + 'cart/change.js', { method: 'POST', headers: json, body: JSON.stringify({ id: line.key, quantity }) });
        // Give Monster's own cart listener time to add or remove gift lines before reloading.
        await new Promise((r) => setTimeout(r, 1800));
      } catch (_) { /* reload shows the true cart */ }
      location.reload();
    };
    // Monster may add/remove gift lines after the page renders; reload once per cart state so rows match.
    const checkRows = async () => {
      if (busy) return;
      try {
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        const rows = document.querySelectorAll('cart-items .cart-item').length;
        const sig = cart.items.map((i) => i.key + ':' + i.quantity).join('|');
        if (rows !== cart.items.length && sessionStorage.getItem('sbCartRowSync') !== sig) {
          sessionStorage.setItem('sbCartRowSync', sig);
          location.reload();
        }
      } catch (_) {}
    };
    setTimeout(checkRows, 2500);
    setTimeout(checkRows, 6000);
    document.addEventListener('change', (e) => {
      const input = e.target.closest?.('cart-items input[name="updates[]"]');
      if (!input) return;
      e.stopImmediatePropagation();
      clearTimeout(pending);
      pending = setTimeout(() => apply(input, Math.max(0, parseInt(input.value, 10) || 0)), 400);
    }, true);
    document.addEventListener('click', (e) => {
      const btn = e.target.closest?.('cart-items cart-remove-button');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const input = btn.closest('.cart-item')?.querySelector('input[name="updates[]"]');
      apply(input || btn, 0);
    }, true);
  }
})();
